// Setup › Your sign-in on the SettingsPage pattern (BL-189): everyone's own sign-in (decision 20, QA-093,
// QA-092), as SignInScreen.tsx draws it in Blueprint, a section per part in one column: the password,
// two-factor with an authenticator app (set up, recovery codes, turn off), the mobile that gets text
// codes, Google or Microsoft as another way in, the devices that skip the second step for 30 days, and a
// code to pair an iPad. A form behind a button opens over the page (a FormSheet, or a FormDialog for a
// code alone); a form shown at rest stays in its section. Every change goes through the api's /me routes,
// which ask for a current code where one is due. Riverstone keeps SignInScreen.tsx.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { RpcError, type SignInMethod } from '@flowology/client';
import {
  Button,
  FormDialog,
  KeyValue,
  Label,
  Link,
  Notice,
  StateMark,
  TextField,
} from '@flowology/ui-web/fl';
import {
  FormSheet,
  SettingFields,
  SettingRow,
  SettingsPage,
  SettingsSection,
} from '@flowology/ui-web/patterns';
import { client, setCsrf } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import {
  deadlineBanner,
  groupedSecret,
  qrPath,
  recoveryNote,
  type RememberedDevice,
  type TwoFactorState,
  type Ways,
} from './signin-model.ts';
import { primeTwoFactorState } from './TwoFactorBanner.tsx';
import './your-sign-in.css';

const LEDE = 'How you sign in, and the second step that keeps it yours.';
const CODE_LABEL = 'A code from your app, or a recovery code';
type Provider = 'google' | 'microsoft';
const NAME: Record<Provider, string> = { google: 'Google', microsoft: 'Microsoft' };

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';
/** The api asks for a fresh second factor first (a code from the app, or a recovery code). */
const askedForStepUp = (e: unknown) =>
  e instanceof RpcError && (e.detail as { reason?: string } | undefined)?.reason === 'step_up_required';
/** A refusal said in the section, in red after StateMark's bar. */
const Refused = ({ text, className }: { text: string; className?: string }) => (
  <span className={className ? `yoursignin-refused ${className}` : 'yoursignin-refused'} role="alert">
    <StateMark state="problem" />
    {text}
  </span>
);
const dayIn = (timeZone: string) => (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' }).format(new Date(iso));

export function YourSignInPage({
  nav,
  timeZone,
  usedRecovery,
  onState,
}: {
  nav: NavProps;
  timeZone: string;
  /** Just signed in with a recovery code: this many are left. */
  usedRecovery?: number;
  /** Each fresh read of where the person stands (the app holds its screens while blocked). */
  onState?: (s: TwoFactorState) => void;
}) {
  const crumb = usePageCrumb(nav);
  const [state, setState] = useState<TwoFactorState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const s = await client.me<TwoFactorState>('GET', '/me/2fa');
      primeTwoFactorState(s);
      setState(s);
      onState?.(s);
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, [onState]);
  useEffect(() => void load(), [load]);
  // Which ways in the person has (a password, Google, Microsoft), for those parts.
  const [ways, setWays] = useState<Ways | null>(null);
  const loadWays = useCallback(
    () =>
      client
        .me<Ways>('GET', '/me/sign-in')
        .then(setWays)
        .catch(() => setWays(null)),
    [],
  );
  useEffect(() => void loadWays(), [loadWays]);
  const banner = state ? deadlineBanner(state) : null;
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage head={{ ...(crumb ? { crumb } : {}), title: 'Your sign-in', lede: LEDE }}>
          {banner?.tone === 'warn' && (
            // while there are days left, the deadline waits on them; past it, or under a day from it, it's
            // a red line in Two-factor sign-in, said at once
            <div role="status">
              <Notice tone="waiting" title={banner.text}>
                {banner.detail}
              </Notice>
            </div>
          )}
          {usedRecovery !== undefined && (
            <Notice tone={usedRecovery <= 2 ? 'waiting' : 'done'} title={recoveryNote(usedRecovery)} />
          )}
          {ways?.setPasswordUntil && (
            <SetNewPassword
              until={ways.setPasswordUntil}
              minLength={ways.minLength}
              hasPassword={ways.password}
              timeZone={timeZone}
              onChanged={loadWays}
            />
          )}
          {ways?.password && (
            <Password
              minLength={ways.minLength}
              changedAt={ways.passwordChangedAt}
              timeZone={timeZone}
              onChanged={loadWays}
            />
          )}
          <TwoFactor
            state={state}
            error={error}
            due={banner?.tone === 'bad' ? `${banner.text} ${banner.detail}` : null}
            onChanged={load}
          />
          {/* a mobile lives on a staff profile: someone without one has none to confirm */}
          {ways?.mobile && (
            <Mobile mobile={ways.mobile} onChanged={() => void Promise.all([loadWays(), load()])} />
          )}
          <Linked ways={ways} onChanged={loadWays} />
          {state?.on && <Remembered timeZone={timeZone} />}
          {ways?.technician && <PairIpad />}
          {/* the AI tools are a page of their own in Setup (the setup audit) */}
          <p className="yoursignin-moved" role="note">
            Your AI tools moved to <Link href="#/aitools">Setup › Your AI tools</Link>.
          </p>
        </SettingsPage>
      </main>
    </div>
  );
}

/** A code from the app (or a recovery code), asked for before a change that needs one. */
function CodeField({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <TextField
      label={label}
      inputMode="numeric"
      autoComplete="one-time-code"
      width="short"
      value={value}
      onChange={onChange}
      {...(autoFocus ? { autoFocus } : {})}
    />
  );
}

/**
 * Set a new password without the old one (the forgotten password's way back): offered for ten minutes
 * after signing in with an emailed code, in that browser. The server decides (set_password_after_code).
 */
function SetNewPassword({
  until,
  minLength,
  hasPassword,
  timeZone,
  onChanged,
}: {
  until: string;
  minLength: number;
  hasPassword: boolean;
  timeZone: string;
  onChanged: () => Promise<void>;
}) {
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const missing =
    next.length < minLength
      ? `It needs at least ${minLength} characters.`
      : again !== next
        ? 'Type the same password twice.'
        : null;
  const by = new Intl.DateTimeFormat('en-CA', { timeZone, timeStyle: 'short' }).format(new Date(until));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.me<{ set: true; csrf: string }>('POST', '/me/password/set', { next });
      // Every session of theirs ended; this browser has a new one.
      setCsrf(r.csrf);
      setDone(true);
      setNext('');
      setAgain('');
      void onChanged();
    } catch (e) {
      setError(e instanceof RpcError ? e.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <SettingsSection
      title="Set a new password"
      {...(done
        ? {}
        : {
            line: `You signed in with an emailed code, so you can ${hasPassword ? 'set a new password' : 'add a password'} without the old one until ${by}. Your other browsers and iPads will be signed out.`,
          })}
    >
      {done ? (
        <Notice tone="done" takesFocus title="Password set. Your other browsers and iPads were signed out." />
      ) : (
        <div className="yoursignin-form">
          <SettingFields label="Set a new password" error={error}>
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={setNext}
            />
            <TextField
              label="The same again"
              type="password"
              autoComplete="new-password"
              value={again}
              onChange={setAgain}
            />
          </SettingFields>
          <div className="yoursignin-acts">
            <Button variant="primary" isBusy={busy} isDisabled={!!missing} onPress={() => void save()}>
              Set password
            </Button>
          </div>
          <p className="yoursignin-missing" role="status" aria-live="polite">
            {missing && !busy ? missing : ''}
          </p>
        </div>
      )}
    </SettingsSection>
  );
}

/**
 * Change your password (QA-093): the current one, a new one, and a code from the app when the api asks
 * for one, in a FormSheet. Every other browser and iPad is signed out; this one stays.
 */
function Password({
  minLength,
  changedAt,
  timeZone,
  onChanged,
}: {
  minLength: number;
  changedAt: string | null;
  timeZone: string;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  return (
    <SettingsSection
      title="Password"
      {...(changedAt ? { line: `Last changed ${dayIn(timeZone)(changedAt)}` } : {})}
      action={
        <Link
          onPress={() => {
            setOpen(true);
            setDone(null);
          }}
        >
          Change password
        </Link>
      }
    >
      {done && <Notice tone="done" takesFocus title={done} />}
      {open && (
        <PasswordSheet
          minLength={minLength}
          onDone={(words) => {
            setDone(words);
            setOpen(false);
            void onChanged();
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </SettingsSection>
  );
}

function PasswordSheet({
  minLength,
  onDone,
  onClose,
}: {
  minLength: number;
  onDone: (words: string) => void;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const missing = !current
    ? 'Type your current password.'
    : next.length < minLength
      ? `The new one needs at least ${minLength} characters.`
      : needCode && !code.trim()
        ? 'Type a code from your app, or a recovery code.'
        : null;
  return (
    <FormSheet
      isOpen
      title="Change password"
      submitLabel="Change password"
      isDirty={!!(current || next || code)}
      isSubmitDisabled={!!missing}
      // the states pass: a primary that can't go yet says what's missing (QA-425)
      {...(missing ? { note: missing } : {})}
      onClose={onClose}
      onSubmit={async () => {
        try {
          const r = await client.me<{ csrf?: string; signedOut: number }>('POST', '/me/password', {
            current,
            next,
            ...(code.trim() ? { code: code.trim() } : {}),
          });
          // Every session of theirs ended with the change (QA-311); this browser has a new one.
          if (r.csrf) setCsrf(r.csrf);
          onDone(
            r.signedOut
              ? 'Password changed. Your other browsers and iPads were signed out.'
              : 'Password changed.',
          );
        } catch (e) {
          if (askedForStepUp(e)) setNeedCode(true);
          setCode('');
          throw new Error(message(e), { cause: e });
        }
      }}
    >
      <TextField
        label="Current password"
        type="password"
        autoComplete="current-password"
        value={current}
        onChange={setCurrent}
      />
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        value={next}
        onChange={setNext}
        isInvalid={next.length > 0 && next.length < minLength}
        description={`At least ${minLength} characters. Your other browsers and iPads will be signed out.`}
      />
      {needCode && <CodeField label={CODE_LABEL} value={code} onChange={setCode} autoFocus />}
    </FormSheet>
  );
}

/** The recovery codes, shown once as they're made, until the person says they've saved them. */
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <Notice
      tone="done"
      takesFocus
      title="Save these recovery codes somewhere safe."
      actions={
        <>
          <Button onPress={() => void navigator.clipboard?.writeText(codes.join('\n'))}>Copy them</Button>
          <Link onPress={onDone}>I’ve saved them</Link>
        </>
      }
    >
      Each signs you in once if you lose your phone. They aren’t shown again.
      <ol className="yoursignin-codes" aria-label="Recovery codes">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
    </Notice>
  );
}

function TwoFactor({
  state,
  error: loadError,
  due,
  onChanged,
}: {
  state: TwoFactorState | null;
  error: string | null;
  /** Past the company's deadline, or under a day from it: what's due, in red at the section's top. */
  due: string | null;
  onChanged: () => Promise<void>;
}) {
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [asking, setAsking] = useState<'off' | 'codes' | null>(null);
  // one action at rest, "Change it", which opens the two choices (setup.js's sign-in)
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const title = 'Two-factor sign-in';
  if (!state)
    return (
      <SettingsSection
        title={title}
        status={loadError ? 'failed' : 'loading'}
        {...(loadError ? { failedText: loadError } : {})}
        onRetry={() => void onChanged()}
      />
    );
  // the deadline's red line first, then a refusal under it
  const note =
    due || error
      ? {
          note: (
            <>
              {due && <Refused text={due} className="yoursignin-due" />}
              {error && <Refused text={error} />}
            </>
          ),
        }
      : {};
  if (codes)
    return (
      <SettingsSection title={title}>
        <RecoveryCodes
          codes={codes}
          onDone={() => {
            setCodes(null);
            void onChanged();
          }}
        />
      </SettingsSection>
    );
  if (!state.on || state.resetHere)
    return (
      <SettingsSection
        title={title}
        line={
          state.resetHere
            ? 'The owner reset your two-factor here. Set up your authenticator app again.'
            : 'Off. After your password or a code, you’ll also give a 6-digit code from an authenticator app on your phone.'
        }
        action={
          <Button
            variant="primary"
            isBusy={busy}
            onPress={() => {
              setBusy(true);
              setError(null);
              client
                .me<{ secret: string; uri: string }>('POST', '/me/2fa/app/start')
                .then(setSetup)
                .catch((e: unknown) => setError(message(e)))
                .finally(() => setBusy(false));
            }}
          >
            Set up an authenticator app
          </Button>
        }
        {...note}
      >
        {setup && (
          <SetupSheet
            setup={setup}
            onDone={(made) => {
              setSetup(null);
              setCodes(made);
              void onChanged(); // on now: the deadline banner goes
            }}
            onClose={() => setSetup(null)}
          />
        )}
      </SettingsSection>
    );
  return (
    <SettingsSection
      title={title}
      line={
        <>
          <Label kind="now">On</Label> with {state.app ? 'your authenticator app' : 'text codes'}.{' '}
          {state.recoveryLeft} recovery {state.recoveryLeft === 1 ? 'code' : 'codes'} left.
        </>
      }
      action={
        !choosing ? (
          <Link onPress={() => setChoosing(true)}>Change it</Link>
        ) : (
          <span className="yoursignin-links">
            <Link onPress={() => setAsking('codes')}>New recovery codes</Link>
            {!state.required && <Link onPress={() => setAsking('off')}>Turn off</Link>}
          </span>
        )
      }
      {...(state.required ? { note: 'Your company requires two-factor for you, so it stays on.' } : {})}
    >
      {asking && (
        <CodeDialog
          title={asking === 'off' ? 'Turn off two-factor?' : 'New recovery codes'}
          submitLabel={asking === 'off' ? 'Turn it off' : 'Make new recovery codes'}
          onSubmit={async (code) => {
            if (asking === 'codes') {
              const r = await client.me<{ recoveryCodes: string[] }>('POST', '/me/2fa/recovery/new', {
                code,
              });
              setCodes(r.recoveryCodes);
            } else {
              await client.me('POST', '/me/2fa/off', { code });
              await onChanged();
            }
            setAsking(null);
            setChoosing(false);
          }}
          onClose={() => setAsking(null)}
        />
      )}
    </SettingsSection>
  );
}

/** A change that a code from the app (or a recovery code) confirms: the code, then the action. */
function CodeDialog({
  title,
  line,
  submitLabel,
  cancelLabel,
  onSubmit,
  onClose,
}: {
  title: string;
  line?: ReactNode;
  submitLabel: string;
  cancelLabel?: string;
  onSubmit: (code: string) => Promise<void>;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  return (
    <FormDialog
      isOpen
      title={title}
      submitLabel={submitLabel}
      {...(cancelLabel ? { cancelLabel } : {})}
      size="md"
      isDirty={false}
      isSubmitDisabled={!code.trim()}
      onClose={onClose}
      onSubmit={async () => {
        try {
          await onSubmit(code.trim());
        } catch (e) {
          setCode('');
          throw new Error(message(e), { cause: e });
        }
      }}
    >
      {line}
      <CodeField label={CODE_LABEL} value={code} onChange={setCode} autoFocus />
    </FormDialog>
  );
}

/** Setting up the app: the QR code and the key, shown once, then the app's first code turns it on. */
function SetupSheet({
  setup,
  onDone,
  onClose,
}: {
  setup: { secret: string; uri: string };
  onDone: (codes: string[]) => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  return (
    <FormSheet
      isOpen
      title="Scan this with your app"
      line="Google Authenticator, Microsoft Authenticator, 1Password… Or type the key. It’s shown once."
      submitLabel="Turn on two-factor"
      isDirty={!!code}
      isSubmitDisabled={!/^\d{6}$/.test(code.trim())}
      onClose={onClose}
      onSubmit={async () => {
        try {
          const r = await client.me<{ recoveryCodes: string[] }>('POST', '/me/2fa/app/confirm', {
            code: code.trim(),
          });
          onDone(r.recoveryCodes);
        } catch (e) {
          throw new Error(message(e), { cause: e });
        }
      }}
    >
      <QrCode uri={setup.uri} />
      <KeyValue label="Key" items={[{ label: 'Key', value: groupedSecret(setup.secret) }]} />
      <CodeField label="The 6-digit code your app shows" value={code} onChange={setCode} />
    </FormSheet>
  );
}

function QrCode({ uri }: { uri: string }) {
  const q = qrPath(uri);
  return (
    // black on white whatever the scheme: a phone's camera reads it so
    <svg
      className="yoursignin-qr"
      viewBox={`0 0 ${q.size} ${q.size}`}
      role="img"
      aria-label="QR code for your authenticator app"
      shapeRendering="crispEdges"
    >
      <rect width={q.size} height={q.size} fill="#fff" />
      <path d={q.d} fill="#000" />
    </svg>
  );
}

function Remembered({ timeZone }: { timeZone: string }) {
  const [devices, setDevices] = useState<RememberedDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setDevices((await client.me<{ devices: RememberedDevice[] }>('GET', '/me/2fa/devices')).devices);
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, []);
  useEffect(() => void load(), [load]);
  const day = dayIn(timeZone);
  const forget = async (id?: string) => {
    try {
      await client.me('POST', '/me/2fa/devices/forget', id ? { id } : {});
      await load();
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <SettingsSection
      title="Remembered devices"
      line="These skip the second step for 30 days. Forget one you don’t use any more."
      {...(devices && devices.length > 1
        ? { action: <Link onPress={() => void forget()}>Forget them all</Link> }
        : {})}
      {...(error && devices ? { note: <Refused text={error} /> } : {})}
      status={devices ? (devices.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
      emptyText="No devices are remembered."
      {...(error ? { failedText: error } : {})}
      onRetry={() => void load()}
    >
      {devices?.map((d) => {
        const name = d.thisDevice ? 'This device' : 'A browser';
        return (
          <SettingRow
            key={d.id}
            name={name}
            help={`remembered ${day(d.rememberedAt)}${d.lastUsedAt ? `, last used ${day(d.lastUsedAt)}` : ''} · until ${day(d.until)}`}
            control={
              <Link label={`Forget ${name.toLowerCase()}`} onPress={() => void forget(d.id)}>
                Forget
              </Link>
            }
          />
        );
      })}
    </SettingsSection>
  );
}

/**
 * The mobile that gets text codes (BL-102): with none on the profile, or to change it, the person types
 * one and gets a code; the profile takes it only once the code is confirmed, so a typo never replaces
 * the mobile they had.
 */
function Mobile({ mobile, onChanged }: { mobile: NonNullable<Ways['mobile']>; onChanged: () => void }) {
  const [sent, setSent] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // typing a mobile: there's none on file, or they asked to use a different one
  const [other, setOther] = useState(false);
  const [phone, setPhone] = useState('');
  const onFile = mobile.tail;
  const typing = !onFile || other;
  const start = (body?: { phone: string }) =>
    void client
      .me<{ phoneTail: string }>('POST', '/me/mobile/start', body)
      .then((r) => {
        setSent(r.phoneTail);
        setCode('');
        setError(null);
      })
      .catch((e: unknown) => setError(message(e)));
  const different = (
    <Link
      onPress={() => {
        setOther(true);
        setSent(null);
        setDone(false);
        setError(null);
      }}
    >
      Use a different mobile
    </Link>
  );
  return (
    <SettingsSection
      title="Your mobile"
      line="Sign-in codes and second-step codes can come by text to your mobile, once you confirm it."
    >
      {done ? (
        <Notice
          tone="done"
          title={`Your mobile ending ${sent} is verified. Codes can come to it by text.`}
          actions={different}
        />
      ) : sent ? (
        <>
          <Notice tone="done" title={`We’ve texted a code to your mobile ending ${sent}.`} />
          <div className="yoursignin-form">
            <SettingFields label="Confirm your mobile" error={error}>
              <CodeField label="Code from the text" value={code} onChange={setCode} />
            </SettingFields>
            <div className="yoursignin-acts">
              <Button
                variant="primary"
                isDisabled={!/^\d{6}$/.test(code.trim())}
                onPress={() =>
                  void client
                    .me('POST', '/me/mobile/confirm', { code: code.trim() })
                    .then(() => {
                      setDone(true);
                      setOther(false);
                      setPhone('');
                      setError(null);
                      onChanged();
                    })
                    .catch((e: unknown) => setError(message(e)))
                }
              >
                Confirm
              </Button>
              {different}
            </div>
          </div>
        </>
      ) : typing ? (
        <form
          className="yoursignin-form"
          onSubmit={(e) => {
            e.preventDefault();
            start({ phone: phone.trim() });
          }}
        >
          <SettingFields label="Your mobile" error={error}>
            <TextField
              label="Mobile number (Canadian or US)"
              type="tel"
              autoComplete="tel"
              value={phone}
              placeholder="604-555-0123"
              maxLength={40}
              onChange={setPhone}
            />
          </SettingFields>
          <div className="yoursignin-acts">
            <Button type="submit" variant="primary" isDisabled={!phone.trim()}>
              Text me a code
            </Button>
            {onFile && <Link onPress={() => setOther(false)}>Cancel</Link>}
          </div>
        </form>
      ) : (
        <Notice
          tone={mobile.verified ? 'done' : 'waiting'}
          title={
            mobile.verified
              ? `Your mobile ending ${onFile} is verified.`
              : `The mobile on your profile ends ${onFile}. Confirm it to get codes by text.`
          }
          actions={
            <>
              {!mobile.verified && <Button onPress={() => start()}>Confirm your mobile</Button>}
              {different}
            </>
          }
        >
          {error && <Refused text={error} />}
        </Notice>
      )}
    </SettingsSection>
  );
}

/** Google or Microsoft as another way in, where the company allows it (decision 19, 20). */
function Linked({ ways, onChanged }: { ways: Ways | null; onChanged: () => Promise<void> }) {
  const [methods, setMethods] = useState<readonly SignInMethod[]>([]);
  const [notice, setNotice] = useState<string | null>(() => {
    const q = new URLSearchParams(window.location.search);
    for (const p of ['google', 'microsoft'] as const) {
      const v = q.get(p);
      if (!v) continue;
      window.history.replaceState(null, '', window.location.pathname);
      return v === 'linked'
        ? `${NAME[p]} is linked: you can sign in with it.`
        : `That ${NAME[p]} account is already someone else’s here.`;
    }
    return null;
  });
  const [error, setError] = useState<string | null>(null);
  // Unlinking asks once ("Unlink Google?"), and for a fresh second factor when the person has one (QA-093).
  const [unlinking, setUnlinking] = useState<Provider | null>(null);
  // Linking asks for a fresh second factor when the person has one (QA-200): the code is confirmed (a
  // step-up), then the provider opens. (Every hook sits above the early return below: React #310.)
  const [linking, setLinking] = useState<Provider | null>(null);
  useEffect(() => {
    client
      .signInMethods()
      .then((r) => setMethods(r.methods))
      .catch(() => setMethods([]));
  }, []);
  const linked = ways?.linked;
  const providers = (['google', 'microsoft'] as const).filter((p) => methods.includes(p) || linked?.[p]);
  if (!providers.length && !notice) return null;
  const link = async (p: Provider, code?: string) => {
    setError(null);
    if (code) await client.me('POST', '/me/2fa/step-up', { code });
    const r = await client.me<{ url: string }>('POST', `/auth/${p}/link`);
    window.location.assign(r.url);
  };
  return (
    <SettingsSection title="Linked sign-ins" {...(error ? { note: <Refused text={error} /> } : {})}>
      {notice && <Notice tone="done" title={notice} />}
      {providers.map((p) => (
        // as the prototype's rows: the name, then what it means for signing in
        <SettingRow
          key={p}
          name={NAME[p]}
          help={linked?.[p] ? 'You can sign in with it' : 'Not linked'}
          control={
            linked?.[p] ? (
              <>
                <Label kind="now">Linked</Label>{' '}
                <Link
                  label={`Unlink ${NAME[p]}`}
                  onPress={() => {
                    setUnlinking(p);
                    setError(null);
                    setNotice(null);
                  }}
                >
                  Unlink
                </Link>
              </>
            ) : (
              <Link
                onPress={() =>
                  void link(p).catch((e: unknown) => {
                    if (askedForStepUp(e)) setLinking(p);
                    else setError(message(e));
                  })
                }
              >
                {`Link ${NAME[p]}`}
              </Link>
            )
          }
        />
      ))}
      {linking && (
        <CodeDialog
          title={`Link ${NAME[linking]}`}
          submitLabel={`Link ${NAME[linking]}`}
          onSubmit={(code) => link(linking, code)}
          onClose={() => setLinking(null)}
        />
      )}
      {unlinking && (
        <UnlinkDialog
          provider={unlinking}
          onDone={() => {
            setUnlinking(null);
            setNotice(`${NAME[unlinking]} is unlinked.`);
            void onChanged();
          }}
          onClose={() => setUnlinking(null)}
        />
      )}
    </SettingsSection>
  );
}

/** Unlink Google?, and a code first when the person has two-factor (QA-093); a refusal stays in it. */
function UnlinkDialog({
  provider,
  onDone,
  onClose,
}: {
  provider: Provider;
  onDone: () => void;
  onClose: () => void;
}) {
  const [needCode, setNeedCode] = useState(false);
  const [code, setCode] = useState('');
  const name = NAME[provider];
  return (
    <FormDialog
      isOpen
      title={`Unlink ${name}?`}
      submitLabel={`Unlink ${name}`}
      cancelLabel="Keep it"
      size="md"
      isDirty={false}
      isSubmitDisabled={needCode && !code.trim()}
      onClose={onClose}
      onSubmit={async () => {
        try {
          await client.me('POST', '/me/sign-in/unlink', {
            provider,
            ...(code.trim() ? { code: code.trim() } : {}),
          });
          onDone();
        } catch (e) {
          // the refusal ("This is your only way to sign in…") or the code ask stays in the dialog
          if (askedForStepUp(e)) setNeedCode(true);
          setCode('');
          throw new Error(message(e), { cause: e });
        }
      }}
    >
      {`You won’t be able to sign in with ${name} until you link it again.`}
      {needCode && <CodeField label={CODE_LABEL} value={code} onChange={setCode} autoFocus />}
    </FormDialog>
  );
}

/** QA-092: a code the iPad takes instead of a sign-in (for companies that sign in only with a provider). */
function PairIpad() {
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A new code ends the old one (QA-092).
  const make = () =>
    client
      .me<{ code: string; expiresAt: string }>('POST', '/me/devices/pairing-code')
      .then((c) => {
        setCode(c);
        setError(null);
      })
      .catch((e: unknown) =>
        setError(
          e instanceof RpcError &&
            (e.detail as { reason?: string } | undefined)?.reason === 'two_factor_setup_required'
            ? 'Set up two-factor above first: your company requires it before an iPad is paired.'
            : message(e),
        ),
      );
  return (
    <SettingsSection
      title="Pair an iPad"
      line="For the tech app: a short code to type on the iPad instead of signing in there."
      action={<Link onPress={() => void make()}>{code ? 'Make a new code' : 'Make a pairing code'}</Link>}
      {...(error ? { note: <Refused text={error} /> } : {})}
    >
      {code && (
        // the code as a tech reads it off the screen: mono, at the largest type
        <Notice
          tone="done"
          takesFocus
          title="Type it on the iPad with your company’s name. It works once, for 10 minutes."
        >
          <KeyValue
            items={[{ label: 'Pairing code', value: <code className="yoursignin-pair">{code.code}</code> }]}
          />
        </Notice>
      )}
    </SettingsSection>
  );
}
